import type {
  AuthMeDTO,
  CompanySettingsDTO,
  CompanySettingsInputDTO,
  AiServiceTestInputDTO,
  AiServiceTestResultDTO,
  CursorPage,
  DashboardDTO,
  SystemMetricsDTO,
  WorkStatsDTO,
  WorkStatsScope,
  EmployeeSettingsDTO,
  EmployeeAiSettingsDTO,
  EmployeeAiServiceInputDTO,
  AiServiceKind,
  EmployeeOptionDTO,
  EventDTO,
  ExtensionTokenDTO,
  ExtensionTokenSummaryDTO,
  JobDTO,
  ImageSearchDetailDTO,
  ImageUrlImportResultDTO,
  JobStatus,
  JsonValue,
  ProductImportResultDTO,
  ProductDetailDTO,
  ProductPacketDTO,
  SizeChartDTO,
  PublishCardDTO,
  ProductSummaryDTO,
  PublishBatchDTO,
  PublishBatchDetailDTO,
  PublishPreviewDTO,
  PublishPricingTemplatesDTO,
  PublishSelectionDTO,
  PublishTargetDTO,
  PurgeEntity,
  PurgeOperationDTO,
  PurgePreviewDTO,
  JobRetryInputDTO,
  JobRetryOperationDTO,
  JobRetryPreviewDTO,
  JobRetrySelectionDTO,
  ShopCreateDTO,
  ShopDTO,
  ShopUnbindResultDTO,
  ShopUnbindBlockersDTO,
  ShopUnbindStopInput,
  ShopUnbindStopResultDTO,
  ShopPatchDTO,
  WBCategoryDTO,
  WbWarehouseDTO,
  WbCardLimitsDTO,
  WbCardSyncStateDTO,
  DxmSessionDTO,
  DxmCategoryNodeDTO,
  DxmCategorySearchResultDTO,
  ShopCleanupOverviewDTO,
  ShopCleanupItemDTO,
  ShopCleanupPolicyDTO,
  ShopCleanupPolicyInputDTO,
  ShopCleanupRunDTO,
  ShopCleanupRunDetailDTO,
  WbOrdersPageDTO,
  SuperAdminCompanyDTO,
  OrderShopDTO,
  OrderProfitQuery,
  OrderProfitPageDTO,
  OrderProfitCostInput,
  OrderProfitCompaniesQuery,
  OrderProfitCompaniesDTO,
  OrderProfitLibraryCandidates,
  OrderProfitLibraryLinkInput,

} from '@wberp/shared';

export type { AuthMeDTO, CompanySettingsDTO, CompanySettingsInputDTO, AiServiceTestInputDTO, AiServiceTestResultDTO, DashboardDTO, EmployeeOptionDTO, EmployeeSettingsDTO, EventDTO, ExtensionTokenDTO, ExtensionTokenSummaryDTO, JobDTO, ProductDetailDTO, ProductPacketDTO, ProductSummaryDTO, ProductImportResultDTO, PublishBatchDTO, PublishBatchDetailDTO, PublishCardDTO, PublishPreviewDTO, PublishSelectionDTO, PublishTargetDTO, ShopDTO, WBCategoryDTO, WbWarehouseDTO, DxmSessionDTO, DxmCategoryNodeDTO };
export type { SizeChartDTO };
export type { JobStatus };
export type { SystemMetricsDTO };
export type { ImageSearchDetailDTO };
export type { PurgeEntity, PurgeOperationDTO, PurgePreviewDTO };
export type { JobRetryInputDTO, JobRetryOperationDTO, JobRetryPreviewDTO, JobRetrySelectionDTO };
export type { JsonValue };
export type { ShopCleanupOverviewDTO, ShopCleanupItemDTO, ShopCleanupPolicyDTO, ShopCleanupPolicyInputDTO, ShopCleanupRunDTO, ShopCleanupRunDetailDTO };
export type { WbOrdersPageDTO };
export type { OrderShopDTO };
export type { OrderProfitCompaniesQuery, OrderProfitCompaniesDTO };
export type { WbCardLimitsDTO, WbCardSyncStateDTO };
export type { WbCardSyncTaskDTO } from '@wberp/shared';

export type ProductQuery = {
  q?: string;
  colorImagePrompt?: string;
  status?: string;
  publication?: 'unpublished';
  deleted?: boolean;
  employeeId?: string;
  companyId?: string;
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
};

export type JobQuery = {
  queue: 'preprocess' | 'publish';
  status?: string;
  productId?: string;
  search?: string;
  companyId?: string;
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
};

export type PublishSelection = PublishSelectionDTO;

export type PublishRequest = {
  selection: PublishSelection;
  shopIds: string[];
  subjectId?: number;
  brand?: string;
  pricingTemplateId?: string;
};

export type PurgeSelection = { ids?: string[]; allMatching?: boolean; filter?: Record<string, unknown>; source?: 'preprocess_jobs' };

export type ShopInput = ShopCreateDTO;
export type ShopPatch = ShopPatchDTO;
export type CompanySettingsInput = CompanySettingsInputDTO;

type Envelope<T> = { data: T };
type ErrorEnvelope = { error?: { code?: string; message?: string; errorId?: string; details?: unknown } };

export class ApiError extends Error {
  readonly errorId: string | undefined;
  readonly code: string | undefined;
  readonly status: number;

  constructor(message: string, status: number, errorId?: string, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errorId = errorId;
    this.code = code;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function queryString(values: Record<string, string | number | boolean | undefined>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && value !== '') query.set(key, String(value));
  }
  const encoded = query.toString();
  return encoded ? `?${encoded}` : '';
}

function requestFailureMessage(status: number): string {
  if (status === 502 || status === 503 || status === 504) return 'ERP 服务暂时不可用，请确认 API 服务已启动后重试。';
  if (status === 401) return '登录状态已失效，请重新登录。';
  if (status === 403) return '当前账号没有执行此操作的权限。';
  return `请求失败（HTTP ${status}）`;
}

export class ApiClient {
  private csrfToken: string | null = null;

  setCsrfToken(token: string | null): void {
    this.csrfToken = token;
  }

  private async request<T>(path: string, options: { method?: string; body?: unknown; csrf?: boolean } = {}): Promise<T> {
    const method = options.method ?? 'GET';
    const headers = new Headers({ Accept: 'application/json' });
    if (options.body !== undefined) headers.set('Content-Type', 'application/json');
    if (options.csrf && this.csrfToken) headers.set('x-csrf-token', this.csrfToken);

    let response: Response;
    try {
      response = await fetch(`/api${path}`, {
        method,
        credentials: 'include',
        headers,
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : '网络请求失败';
      throw new ApiError(`无法连接 ERP 服务：${message}`, 0);
    }

    const payload: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const errorValue: ErrorEnvelope = isRecord(payload) ? payload as ErrorEnvelope : {};
      throw new ApiError(
        errorValue.error?.message ?? requestFailureMessage(response.status),
        response.status,
        errorValue.error?.errorId,
        errorValue.error?.code,
      );
    }
    if (!isRecord(payload) || !('data' in payload)) {
      throw new ApiError('服务端响应格式不符合接口契约', response.status);
    }
    return (payload as Envelope<T>).data;
  }

  getAuth(): Promise<AuthMeDTO> { return this.request('/auth/me'); }
  getSuperAdminCompanies(): Promise<{ items: SuperAdminCompanyDTO[]; activeCompanyId: string | null }> { return this.request('/auth/super-admin/companies'); }
  switchSuperAdminCompany(companyId: string | null): Promise<{ companyId: string | null; companyName: string | null }> {
    return this.request('/auth/super-admin/switch-company', { method: 'POST', body: { companyId }, csrf: true });
  }
  login(input: { loginAccount: string; password: string; role?: 'employee' | 'company_admin' }): Promise<{ user: AuthMeDTO['user']; csrfToken: string }> {
    return this.request('/auth/login', { method: 'POST', body: input, csrf: false });
  }
  logout(): Promise<unknown> { return this.request('/auth/logout', { method: 'POST', body: {}, csrf: true }); }
  getDashboard(companyId?: string): Promise<DashboardDTO> { return this.request(`/dashboard${queryString({ companyId })}`); }
  getSystemMetrics(forceRefresh = false): Promise<SystemMetricsDTO> { return this.request(`/system-metrics${forceRefresh ? '?refresh=1' : ''}`); }
  getWorkStats(scope: WorkStatsScope, companyId?: string): Promise<WorkStatsDTO> { return this.request(`/work-stats${queryString({ scope, companyId })}`); }
  getProducts(query: ProductQuery = {}): Promise<CursorPage<ProductSummaryDTO>> {
    return this.request(`/products${queryString({ ...query, deleted: query.deleted ? true : undefined, limit: query.limit ?? 30 })}`);
  }
  importProducts(urls: string[], employeeId?: string, isApparel = false): Promise<ProductImportResultDTO> {
    return this.request('/products/import', { method: 'POST', body: { urls, isApparel, ...(employeeId ? { employeeId } : {}) }, csrf: true });
  }
  getProduct(id: string): Promise<ProductDetailDTO> { return this.request(`/products/${encodeURIComponent(id)}`); }
  importImage(imageBase64: string, fileName: string, employeeId?: string, collectSimilar = false, isApparel = false): Promise<ProductImportResultDTO> {
    return this.request('/products/import-image', { method: 'POST', body: { imageBase64, fileName, collectSimilar, isApparel, ...(employeeId ? { employeeId } : {}) }, csrf: true });
  }
  importImageUrl(imageUrl: string, employeeId?: string, collectSimilar = false, isApparel = false): Promise<ProductImportResultDTO> {
    return this.request('/products/import-image-url', { method: 'POST', body: { imageUrl, collectSimilar, isApparel, ...(employeeId ? { employeeId } : {}) }, csrf: true });
  }
  importImageUrls(imageUrls: string[], employeeId: string | undefined, collectSimilar: boolean, batchId: string, isApparel = false): Promise<ImageUrlImportResultDTO> {
    return this.request('/products/import-image-urls', { method: 'POST', body: { batchId, imageUrls, collectSimilar, isApparel, ...(employeeId ? { employeeId } : {}) }, csrf: true });
  }
  searchProductImage(id: string, employeeId?: string, isApparel = false): Promise<ProductImportResultDTO> {
    return this.request(`/products/${encodeURIComponent(id)}/image-search`, { method: 'POST', body: { isApparel, ...(employeeId ? { employeeId } : {}) }, csrf: true });
  }
  getImageSearch(jobId: string): Promise<ImageSearchDetailDTO> { return this.request(`/jobs/${encodeURIComponent(jobId)}/image-search`); }
  preprocessWbProduct(id: string): Promise<{ jobId: string }> {
    return this.request(`/products/${encodeURIComponent(id)}/preprocess-wb`, { method: 'POST', body: {}, csrf: true });
  }
  createVersion(id: string, expectedVersion: number, packet: JsonValue): Promise<unknown> {
    return this.request(`/products/${encodeURIComponent(id)}/versions`, { method: 'POST', body: { expectedVersion, packet }, csrf: true });
  }
  getSizeChartSources(id: string): Promise<{ items: string[] }> {
    return this.request(`/products/${encodeURIComponent(id)}/size-chart/sources`);
  }
  recognizeSizeChart(id: string, versionId: string, imageUrls?: string[]): Promise<{ sizeChart: SizeChartDTO }> {
    return this.request(`/products/${encodeURIComponent(id)}/size-chart/recognize`, { method: 'POST', body: { versionId, ...(imageUrls ? { imageUrls } : {}) }, csrf: true });
  }
  renderSizeChart(id: string, versionId: string, sizeChart: SizeChartDTO): Promise<{ dataUrl: string }> {
    return this.request(`/products/${encodeURIComponent(id)}/size-chart/render`, { method: 'POST', body: { versionId, sizeChart }, csrf: true });
  }
  estimateSizeChart(id: string, versionId: string, sizeChart: SizeChartDTO): Promise<{ sizeChart: SizeChartDTO }> {
    return this.request(`/products/${encodeURIComponent(id)}/size-chart/estimate`, { method: 'POST', body: { versionId, sizeChart }, csrf: true });
  }
  deleteProduct(id: string): Promise<unknown> { return this.request(`/products/${encodeURIComponent(id)}/delete`, { method: 'POST', body: {}, csrf: true }); }
  restoreProduct(id: string): Promise<unknown> { return this.request(`/products/${encodeURIComponent(id)}/restore`, { method: 'POST', body: {}, csrf: true }); }
  getJobs(query: JobQuery): Promise<CursorPage<JobDTO>> {
    return this.request(`/jobs${queryString({ ...query, limit: query.limit ?? 30 })}`);
  }
  jobAction(id: string, action: 'pause' | 'resume' | 'retry' | 'cancel', options?: { confirmWbCardRejectionResolved?: boolean }): Promise<unknown> {
    return this.request(`/jobs/${encodeURIComponent(id)}/${action}`, { method: 'POST', body: options ?? {}, csrf: true });
  }
  getEmployees(cursor?: string): Promise<CursorPage<EmployeeOptionDTO>> {
    return this.request(`/employees${queryString({ cursor, limit: 100 })}`);
  }
  getShops(): Promise<CursorPage<ShopDTO>> { return this.request('/shops?limit=100'); }
  getCompanyShops(): Promise<CursorPage<ShopDTO>> { return this.request('/shops?scope=company&limit=100'); }
  getManagedShops(): Promise<CursorPage<ShopDTO>> { return this.request('/shops?scope=managed&limit=100'); }
  getShopCardLimits(shopId: string): Promise<WbCardLimitsDTO> { return this.request(`/shops/${encodeURIComponent(shopId)}/wb-card-limits`); }
  getShopCardSyncState(runId?: string): Promise<WbCardSyncStateDTO> { return this.request(`/shops/wb-card-sync${queryString({runId})}`); }
  syncShopCardLimits(shopId: string): Promise<WbCardSyncStateDTO> { return this.request(`/shops/${encodeURIComponent(shopId)}/wb-card-limits/sync`, { method: 'POST', body: {}, csrf: true }); }
  getOrderShops(companyId?: string): Promise<{ items: OrderShopDTO[] }> { return this.request(`/orders/shops${queryString({ companyId })}`); }
  getOrders(query: { from: string; to: string; shopId?: string; wbStatus?: string; companyId?: string; page?: number }): Promise<WbOrdersPageDTO> {
    return this.request(`/orders${queryString(query)}`);
  }
  refreshOrders(shopId?: string): Promise<{ requested: number; requestedAt: string }> {
    return this.request('/orders/refresh', { method: 'POST', body: shopId ? { shopId } : {}, csrf: true });
  }
  getOrderProfit(query: OrderProfitQuery): Promise<OrderProfitPageDTO> {
    return this.request(`/order-profit${queryString(query)}`);
  }
  getOrderProfitCompanies(query: OrderProfitCompaniesQuery): Promise<OrderProfitCompaniesDTO> {
    return this.request(`/order-profit/companies${queryString(query)}`);
  }
  saveOrderProfitCost(input: OrderProfitCostInput): Promise<{ saved: true }> {
    return this.request('/order-profit/costs', { method: 'PUT', body: input, csrf: true });
  }
  getOrderProfitLibraryCandidates(shopId: string, wbOrderId: string, search?: string): Promise<OrderProfitLibraryCandidates> {
    const params = new URLSearchParams({ shopId, wbOrderId });
    if (search?.trim()) params.set('search', search.trim().slice(0, 120));
    return this.request(`/order-profit/library-candidates?${params.toString()}`);
  }
  saveOrderProfitLibraryLink(input: OrderProfitLibraryLinkInput): Promise<{ saved: true }> {
    return this.request('/order-profit/library-link', { method: 'PUT', body: input, csrf: true });
  }
  getShopEmployees(): Promise<CursorPage<EmployeeOptionDTO>> { return this.request('/shops/employees'); }
  getShopCleanup(): Promise<ShopCleanupOverviewDTO> { return this.request('/shop-cleanup'); }
  saveShopCleanupPolicy(input: ShopCleanupPolicyInputDTO): Promise<ShopCleanupPolicyDTO> {
    return this.request('/shop-cleanup/policy', { method: 'PUT', body: input, csrf: true });
  }
  startShopCleanup(input: { mode: 'preview' | 'execute'; idempotencyKey: string }): Promise<{ items: ShopCleanupRunDTO[] }> {
    return this.request('/shop-cleanup/runs', { method: 'POST', body: input, csrf: true });
  }
  getShopCleanupRuns(cursor?: string): Promise<CursorPage<ShopCleanupRunDTO>> {
    return this.request(`/shop-cleanup/runs${queryString({ cursor, limit: 20 })}`);
  }
  getShopCleanupRun(id: string, cursor?: string): Promise<ShopCleanupRunDetailDTO> {
    return this.request(`/shop-cleanup/runs/${encodeURIComponent(id)}${queryString({ cursor, limit: 30 })}`);
  }
  cancelShopCleanupRun(id: string): Promise<ShopCleanupRunDTO> {
    return this.request(`/shop-cleanup/runs/${encodeURIComponent(id)}/cancel`, { method: 'POST', body: {}, csrf: true });
  }
  recheckShopCleanupRun(id: string): Promise<ShopCleanupRunDTO> {
    return this.request(`/shop-cleanup/runs/${encodeURIComponent(id)}/recheck`, { method: 'POST', body: {}, csrf: true });
  }
  createShop(input: ShopInput): Promise<ShopDTO> { return this.request('/shops', { method: 'POST', body: input, csrf: true }); }
  updateShop(id: string, patch: ShopPatch): Promise<ShopDTO> { return this.request(`/shops/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch, csrf: true }); }
  unbindShop(id: string): Promise<ShopUnbindResultDTO> { return this.request(`/shops/${encodeURIComponent(id)}/unbind`, { method: 'POST', body: {}, csrf: true }); }
  getShopUnbindBlockers(id: string, cursor?: string): Promise<ShopUnbindBlockersDTO> { return this.request(`/shops/${encodeURIComponent(id)}/unbind-blockers${queryString({ cursor, limit: 30 })}`); }
  stopShopUnbindBlocker(id: string, input: ShopUnbindStopInput): Promise<ShopUnbindStopResultDTO> { return this.request(`/shops/${encodeURIComponent(id)}/unbind-blockers/stop`, { method: 'POST', body: input, csrf: true }); }
  getWbWarehouses(shopId: string): Promise<{ items: WbWarehouseDTO[] }> { return this.request(`/wb/warehouses?shopId=${encodeURIComponent(shopId)}`); }
  probeWbWarehouses(input: { token: string; shopId?: string; commonBaseUrl?: string | null; marketplaceBaseUrl?: string | null }): Promise<{ sellerId: string; items: WbWarehouseDTO[]; alreadyBound: boolean }> {
    return this.request('/wb/warehouses', { method: 'POST', body: input, csrf: true });
  }
  previewPublish(input: PublishRequest): Promise<PublishPreviewDTO> { return this.request('/publish/preview', { method: 'POST', body: input, csrf: true }); }
  getPublishPricingTemplates(employeeId?: string): Promise<PublishPricingTemplatesDTO> {
    return this.request(`/publish/pricing-templates${employeeId ? `?employeeId=${encodeURIComponent(employeeId)}` : ''}`);
  }
  createPublishBatch(input: PublishRequest & { idempotencyKey: string }): Promise<PublishBatchDTO> {
    return this.request('/publish/batches', { method: 'POST', body: input, csrf: true });
  }
  getPublishBatches(query: { cursor?: string; companyId?: string; status?: string; from?: string; to?: string } = {}): Promise<CursorPage<PublishBatchDTO>> {
    return this.request(`/publish/batches${queryString({ ...query, limit: 30 })}`);
  }
  getPublishBatch(id: string, cursor?: string): Promise<PublishBatchDetailDTO> {
    return this.request(`/publish/batches/${encodeURIComponent(id)}${queryString({ cursor, limit: 30 })}`);
  }
  getPublishTargetCards(id: string, cursor?: string): Promise<CursorPage<PublishCardDTO>> {
    return this.request(`/publish/targets/${encodeURIComponent(id)}/cards${queryString({ cursor, limit: 30 })}`);
  }
  getWbCategories(q?: string, cursor?: string, shopId?: string): Promise<CursorPage<WBCategoryDTO>> {
    return this.request(`/wb/categories${queryString({ q, cursor, shopId, limit: 30 })}`);
  }
  getDxmSession(): Promise<DxmSessionDTO> { return this.request('/dxm/session'); }
  async getDxmCookieForCopy(): Promise<string> {
    const headers = new Headers({ Accept: 'text/plain, application/json' });
    if (this.csrfToken) headers.set('x-csrf-token', this.csrfToken);
    let response: Response;
    try {
      response = await fetch('/api/dxm/session/copy-cookie', { method: 'POST', credentials: 'include', cache: 'no-store', headers });
    } catch (error) {
      const message = error instanceof Error ? error.message : '网络请求失败';
      throw new ApiError(`无法连接 ERP 服务：${message}`, 0);
    }
    if (!response.ok) {
      const payload: unknown = await response.json().catch(() => null);
      const errorValue: ErrorEnvelope = isRecord(payload) ? payload as ErrorEnvelope : {};
      throw new ApiError(errorValue.error?.message ?? requestFailureMessage(response.status), response.status, errorValue.error?.errorId, errorValue.error?.code);
    }
    return response.text();
  }
  importDxmCookie(cookie: string): Promise<DxmSessionDTO> { return this.request('/dxm/session/import', { method: 'POST', body: { cookie }, csrf: true }); }
  checkDxmLogin(): Promise<DxmSessionDTO> { return this.request('/dxm/session/check', { method: 'POST', body: {}, csrf: true }); }
  logoutDxm(): Promise<DxmSessionDTO> { return this.request('/dxm/session/logout', { method: 'POST', body: {}, csrf: true }); }
  getDxmCategories(parentCategoryId?: number): Promise<{ items: DxmCategoryNodeDTO[] }> { return this.request(`/dxm/wb/categories${queryString({ parentCategoryId })}`); }
  searchDxmCategories(q: string): Promise<DxmCategorySearchResultDTO> { return this.request(`/dxm/wb/categories/search${queryString({ q })}`); }
  getDxmCategory(categoryId: number, shopId: string): Promise<DxmCategoryNodeDTO> { return this.request(`/dxm/wb/category${queryString({ categoryId, shopId })}`); }
  repairTarget(id: string, versionId: string): Promise<unknown> {
    return this.request(`/publish/targets/${encodeURIComponent(id)}/repair`, { method: 'POST', body: { versionId }, csrf: true });
  }
  getCompanySettings(): Promise<CompanySettingsDTO> { return this.request('/settings/company'); }
  putCompanySettings(input: CompanySettingsInput): Promise<CompanySettingsDTO> { return this.request('/settings/company', { method: 'PUT', body: input, csrf: true }); }
  testCompanyAi(input: AiServiceTestInputDTO): Promise<AiServiceTestResultDTO> { return this.request('/settings/company/ai-test', { method: 'POST', body: input, csrf: true }); }
  getEmployeeAiSettings(): Promise<EmployeeAiSettingsDTO> { return this.request('/settings/employee/ai'); }
  putEmployeeAiService(kind: AiServiceKind, input: EmployeeAiServiceInputDTO): Promise<EmployeeAiSettingsDTO> { return this.request(`/settings/employee/ai/${kind}`, { method: 'PUT', body: input, csrf: true }); }
  testEmployeeAi(input: AiServiceTestInputDTO): Promise<AiServiceTestResultDTO> { return this.request('/settings/employee/ai-test', { method: 'POST', body: input, csrf: true }); }
  getEmployeeSettings(): Promise<EmployeeSettingsDTO> { return this.request('/settings/employee'); }
  putEmployeeSettings(input: EmployeeSettingsDTO): Promise<EmployeeSettingsDTO> { return this.request('/settings/employee', { method: 'PUT', body: input, csrf: true }); }
  getEvents(query: Record<string, string | number | undefined>): Promise<CursorPage<EventDTO>> {
    return this.request(`/events${queryString({ ...query, limit: query.limit ?? 30 })}`);
  }
  exportEvents(query: Record<string, string | number | undefined>): Promise<{ blob: Blob; truncated: boolean; exportedCount: number; limit: number }> {
    return this.requestBlob(`/events/export${queryString(query)}`);
  }
  issueExtensionToken(): Promise<ExtensionTokenDTO> { return this.request('/auth/extension-token', { method: 'POST', body: {}, csrf: true }); }
  getExtensionTokens(): Promise<CursorPage<ExtensionTokenSummaryDTO>> { return this.request('/auth/extension-tokens?limit=100'); }
  revokeExtensionToken(id: string): Promise<unknown> { return this.request(`/auth/extension-tokens/${encodeURIComponent(id)}`, { method: 'DELETE', csrf: true }); }
  previewPurge(entity: PurgeEntity, selection: PurgeSelection): Promise<PurgePreviewDTO> {
    return this.request(`/${entity === 'product' ? 'products' : entity === 'job' ? 'jobs' : 'publish/batches'}/purge/preview`, { method: 'POST', body: selection, csrf: true });
  }
  purge(entity: PurgeEntity, selection: PurgeSelection, selectionHash: string): Promise<PurgeOperationDTO> {
    return this.request(`/${entity === 'product' ? 'products' : entity === 'job' ? 'jobs' : 'publish/batches'}/purge`, { method: 'POST', body: { ...selection, selectionHash }, csrf: true });
  }
  getPurgeOperation(id: string): Promise<PurgeOperationDTO> { return this.request(`/purge-operations/${encodeURIComponent(id)}`); }
  createJobRetry(input: JobRetryInputDTO): Promise<JobRetryOperationDTO> {
    return this.request('/job-retries', { method: 'POST', body: input, csrf: true });
  }
  previewJobRetry(input: Pick<JobRetryInputDTO, 'queue' | 'selection'>): Promise<JobRetryPreviewDTO> {
    return this.request('/job-retries/preview', { method: 'POST', body: { queue: input.queue, selection: input.selection }, csrf: true });
  }
  getJobRetries(queue: JobRetryInputDTO['queue']): Promise<{ items: JobRetryOperationDTO[] }> {
    return this.request(`/job-retries${queryString({ queue })}`);
  }
  getJobRetry(id: string): Promise<JobRetryOperationDTO> {
    return this.request(`/job-retries/${encodeURIComponent(id)}`);
  }

  private async requestBlob(path: string): Promise<{ blob: Blob; truncated: boolean; exportedCount: number; limit: number }> {
    const headers = new Headers({ Accept: 'text/csv, application/json' });
    if (this.csrfToken) headers.set('x-csrf-token', this.csrfToken);
    const response = await fetch(`/api${path}`, { credentials: 'include', headers });
    if (!response.ok) {
      const payload: unknown = await response.json().catch(() => null);
      const errorValue: ErrorEnvelope = isRecord(payload) ? payload as ErrorEnvelope : {};
      throw new ApiError(errorValue.error?.message ?? `导出失败（HTTP ${response.status}）`, response.status, errorValue.error?.errorId);
    }
    return {
      blob: await response.blob(),
      truncated: response.headers.get('x-wberp-export-truncated') === 'true',
      exportedCount: Number(response.headers.get('x-wberp-export-count') ?? 0),
      limit: Number(response.headers.get('x-wberp-export-limit') ?? 0),
    };
  }
}

export const api = new ApiClient();
